import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { Country, InputRuleApiModel, MarketplaceOrderApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SumNumber } from '../../lib/helpers/ArrayHelper';
import Loading from '../../../../components/loading/Loading';
import { ContentsSelectionRow } from './ContentsSelectionRow';
import { GetCollatedCustomerReferenceNumbers } from './item/OrderReferenceNumberUtils';
import { P2GBox, P2GGrid, P2GDivider, P2GButton } from '@parcel2go/core-ui';

import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ContentSummaryTable } from './ContentSummaryTable';

interface props {
  onSave?: (value: number, items: MarketplaceOrderApiModel[]) => void;
  onUpdate?: (value: number, items: MarketplaceOrderApiModel[]) => void;
  close?: ReactNode;
  items: MarketplaceOrderApiModel[];
  countries: () => Promise<Country[]>;
  getValidation: () => Promise<{ ProductValidation: InputRuleApiModel[] }>;
  tariffSystem: string;
  totalWeight: number;
  currencyCode: string;
  item: SmartSendItemApiModel;
  openContents?: ReactNode;
}

export const ContentsSelection: React.FC<props> = (props) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [validation, setValidation] = useState<{ ProductValidation?: InputRuleApiModel[] }>();
  const hasMultipleOrderNumbers = useMemo(
    () => GetCollatedCustomerReferenceNumbers(props.items).length > 1,
    [props.items]
  );
  const [contentsIsOpen, setContentsIsOpen] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  // Derived rather than held in state: the rows render from props.items, so a separate copy of the
  // total could only ever disagree with what is on screen. See the note on `update`.
  const total = SumNumber(props.items.map((x) => x.Value));

  useEffect(() => {
    props.countries().then(setCountries);
    props.getValidation().then((x) => setValidation(x || { ProductValidation: [] }));
  }, []);

  // Reports the line total up once on open, which is what reconciles item.Value against the lines when
  // the panel is first shown. Mount-only by design - edits report through `update` instead of through a
  // state-change effect.
  useEffect(() => {
    props.onUpdate?.(SumNumber(props.items.map((x) => x.Value)), props.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Builds the next array from props and reports it straight up, instead of mutating a state copy.
   *
   * What was here before kept `items` in state, seeded from props.items once with no resync, rendered
   * the rows from props.items, and mutated the state copy in place with Object.assign. Those three only
   * agree on the first edit: ItemDetails.onContentUpdate rebuilds MarketplaceOrderLines into NEW objects
   * on every keystroke, so from the second keystroke onward the state array held orphaned objects and
   * props.items - the array actually being rendered - was never written to at all.
   *
   * The visible symptom was a caret jump. setItems triggered a render in which the input's value prop
   * was one character behind the DOM, so React wrote the stale string back into the node and the cursor
   * snapped to the end; the effect then landed the correct text a moment later, leaving it there. Typing
   * anywhere but the end of a description or SKU moved the cursor to the end. The Value field escaped it
   * only because ProductContentFields keeps its own valueText state and renders from that.
   */
  const update = (index: number, item: MarketplaceOrderApiModel) => {
    const next = props.items.map((row, i) => (i === index ? { ...row, ...item } : row));
    props.onUpdate?.(SumNumber(next.map((x) => x.Value)), next);
  };

  return (
    <P2GBox>
      {contentsIsOpen && <P2GBox mb={3}>{props.openContents}</P2GBox>}
      <ContentSummaryTable totalValue={total} totalWeight={props.totalWeight} currencyCode={props.currencyCode}>
        <P2GGrid container direction="column" spacing={2}>
          {props.items.map((item, index) => (
            <Fragment key={`${index}_${item.Id}`}>
              {index > 0 && (
                <P2GGrid item>
                  <P2GDivider />
                </P2GGrid>
              )}
              <P2GGrid item>
                {validation ? (
                  <ContentsSelectionRow
                    onOpen={() => setContentsIsOpen(true)}
                    item={props.item}
                    productValidation={validation.ProductValidation || []}
                    marketplaceOrder={item}
                    onUpdate={(updated) => update(index, updated)}
                    countries={countries}
                    tariffSystem={props.tariffSystem}
                    showReferenceNumber={hasMultipleOrderNumbers}
                    currencyCode={props.currencyCode}
                  />
                ) : (
                  <Loading />
                )}
              </P2GGrid>
            </Fragment>
          ))}
        </P2GGrid>
      </ContentSummaryTable>
      {props.onSave && (
        <P2GBox mt={2} textAlign="center">
          <P2GButton onClick={() => {}}>{t('SAVE_BUTTON', { defaultValue: 'Save' })}</P2GButton>
        </P2GBox>
      )}
    </P2GBox>
  );
};
